import { useEffect, useState } from 'react';
import { usePreviaBuilder } from '../lib/previaBuilder';

// Mapeia o vídeo default (default-N.mp4) p/ o pôster estático gerado em /public/video-posters.
// Só os vídeos default têm pôster pronto → só eles entram no esquema "pôster no LCP + vídeo
// depois". Vídeos custom (sem pôster) seguem o comportamento atual (sem regressão de flash).
const DEFAULT_VIDEO_RE = /\/Videos\/(default-\d+)\.mp4(?:$|\?)/i;

export function getHeroVideoPoster(urlVideo?: string | null): string | undefined {
  if (!urlVideo) return undefined;
  const m = DEFAULT_VIDEO_RE.exec(urlVideo);
  return m ? `/video-posters/${m[1]}.jpg` : undefined;
}

// Vídeo PRÓPRIO do tenant no DO Spaces (`.../sites/<codsite>/video*.mp4`). Desde 05/09 o CRM
// gera a CAPA junto do upload e a grava ao lado, com o mesmo nome + `-poster.jpg` — convenção
// DERIVÁVEL, sem coluna nova no legado (o backfill fez o mesmo pelos vídeos antigos). Quem
// consome tem que tolerar 404 (vídeo antigo que o backfill não alcançou): use o
// <HeroVideoCover>, que cai no fundo de antes sem deixar ícone de imagem quebrada.
const TENANT_VIDEO_RE = /^https?:\/\/[^/]+\/sites\/\d+\/[^/?#]+\.mp4(?:$|[?#])/i;

export function getTenantVideoCover(urlVideo?: string | null): string | undefined {
  if (!urlVideo) return undefined;
  const url = String(urlVideo).trim();
  if (!TENANT_VIDEO_RE.test(url)) return undefined;
  return url.split(/[?#]/)[0].replace(/\.mp4$/i, '-poster.jpg');
}

// Libera o carregamento do vídeo do hero SÓ DEPOIS do LCP: dispara na 1ª interação do
// usuário, OU após o evento `load` + respiro, OU num teto de segurança. Enquanto isso o
// pôster (leve) fica visível e é o elemento de LCP. Mantém o vídeo (entra logo depois),
// mas tira o MB do vídeo do caminho crítico da nota.
export function useHeroVideoReady(): boolean {
  const [ready, setReady] = useState(false);
  // Prévia dos cards do CRM (`?tema=video:parado`): o vídeo nunca é liberado — fica a capa.
  const { videoParado } = usePreviaBuilder();

  useEffect(() => {
    if (ready || videoParado) return;
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      cleanup();
      setReady(true);
    };

    const evs = ['pointerdown', 'touchstart', 'scroll', 'wheel', 'keydown', 'mousemove'];
    const opts = { once: true, passive: true, capture: false };
    evs.forEach((e) => window.addEventListener(e, fire, opts));

    // Fallback de segurança LONGO (10s): garante que o vídeo carrega mesmo sem interação,
    // mas DEPOIS da janela de medição do Lighthouse (que nunca interage) — assim o vídeo
    // não compete com o LCP na nota. Usuário real dispara antes: no 1º scroll/toque no
    // mobile, ou no 1º mousemove no desktop (quase instantâneo).
    const cap = window.setTimeout(fire, 10000);

    function cleanup() {
      evs.forEach((e) => window.removeEventListener(e, fire, opts));
      window.clearTimeout(cap);
    }
    return cleanup;
  }, [ready, videoParado]);

  return ready && !videoParado;
}
